<template>
<DxDataGrid v-if="isValidTab" :show-borders="true" :data-source="subgridData" :columns="dataColumns" ref="subgridComponent">
    <DxPaging :enabled="true" :page-size="10" />
    <DxHeaderFilter :visible="true" />
    <DxEditing :allow-adding="true" :allow-updating="true" :allow-deleting="true" mode="row" />
    <DxToolbar>
        <DxItem name="addOrLinkRowButton" location="after" widget="dxDropDownButton"
			:options="{ icon: 'plus', displayExpr: 'text', wrapItemText: true, dropDownOptions: { width: '170px' },items: [
				{ text: 'Create New', icon: 'plus', onClick: addNewRowClicked },
				{ text: 'Link Existing', icon: 'link', onClick: linkExistingRowClicked }
			] }"
        />
    </DxToolbar>
	<template #columnFileEdit="{ data }">
		<div :class="data.cellElement !== null ? '' : 'dx-card'">
			<div v-if="data.cellElement === null" class="dx-card-title">{{ data.column.caption }}</div>
			<div v-if="data.cellElement === null" style="height:10px;"></div>
			<DxFileUploader upload-mode="instantly" :upload-url="appInfo.baseURL + '/attachment'" :upload-headers="{ 'accept': 'application/json' }"
				@uploaded="onFileUploaded(data, $event)" @value-changed="onFileValueChanged(data, $event)" :value="data.value && data.value.id ? [{ name: data.value.filename, size: data.value.filesize }] : []"
			/>
		</div>
	</template>
	<template #columnFileRender="{ data }">
		<a v-if="data.value && data.value.store" :href="appInfo.storageURL + '/' + data.value.store" target="_blank">{{ data.text }}</a>
	</template>
	<template #columnReferenceEdit="{ data }">
		<ReferenceSelector :relationship-details="data.column['_relationshipDetails']" :label="data.column.caption" :is-row-editor="data.cellElement !== null"
			:existing="data.value" @updated="onReferenceValueChanged(data, $event)" />
	</template>
	<template #columnReferenceRender="{ data }">
		<template v-if="data.value && Array.isArray(data.value)">
			<a class="reference-link" v-for="item in data.value" :key="item.id" href="#" @click.prevent.stop="onReferenceClicked(item)">{{ item.name || item.title || item.code || ('#' + item.mote_id) || ('#' + item.id) }}</a>
		</template>
		<template v-else>
			<span></span>
		</template>
	</template>
</DxDataGrid>
<div v-else style="padding: 1em 0;">
    <div class="dx-error-message">
        <i class="dx-icon-warning"></i><div class="dx-error-message-text">Error rendering subgrid. Check that the relationship is setup properly in the tab details.</div>
    </div>
</div>
<DxPopup v-model:visible="showReferenceSelector" :show-title="true" :show-close-button="true" :width="500" :height="230" :title="'Link existing ' + (sourceRelationshipLinkMeta?.label ?? 'item')" :defer-rendering="true">
	<ReferenceSelector :relationship-details="sourceRelationshipLinkMeta?.details" :force-single="true" @updated="lastReferenceSelected = $event"/>
	<DxButton :text="'Link ' + sourceRelationshipLinkMeta?.label ?? 'item'" style="display: block; margin: 1em auto 0;" @click="executeLinkExistingRow" />
</DxPopup>
</template>

<script setup>
/* eslint-disable */
import { defineProps, computed, inject, watch, ref } from 'vue';
import { useRouter } from 'vue-router';
import { DxDataGrid, DxPaging, DxHeaderFilter, DxEditing, DxToolbar, DxItem } from 'devextreme-vue/data-grid';
import { DxPopup } from 'devextreme-vue/popup';
import { DxButton } from 'devextreme-vue/button';
import DataSource from "devextreme/data/data_source"
import { SchemaCache, getDatagridColumnsFromSchema } from "@/schemas";
import { fieldNamesReserved } from '@/fields';
import ReferenceSelector from '@/components/reference-selector.vue';
import DxFileUploader from 'devextreme-vue/file-uploader';
import { alert as dxAlert } from 'devextreme/ui/dialog';

const $http = inject('$http');
const props = defineProps([
    'preview',
	'settings',
	'context',
    'tab',
    'parentRow',
]);
const router = useRouter();

const dataColumns = ref([]);
const subgridComponent = ref(null);

const showReferenceSelector = ref(false);
const lastReferenceSelected = ref(null);

// Return if this is a valid tab with sufficent information to load.
const isValidTab = computed(() => {
    return props.tab && props.tab.type == 'datagrid' && sourceRelationship.value;
});
// Return relationship information with schema/field, or null if not valid.
const sourceRelationship = computed(() => {
    if (!props.tab || !props.tab.relationship)
        return null;
    // Relationship should be the target field in format 'customer.orders'.
    const relationship = SchemaCache.getByKeyspec(props.tab.relationship);
    const details = relationship ? relationship.getRelationshipDetails() : null;
    // TODO: Deal with multiple opposite schemas.
    if (details && details.oppositeSchemas.length > 1) {
        console.error('Multiple opposite schemas found for relationship; unimplemented!');
        return null;
    }
    // console.log("Parent Row:", props.parentRow);
    // console.log("Relationship:", details);

    return !details ? null : details;
});

// Return the type and label of the schemas that could be used to link to for this subgrid (instead of adding new)
const sourceRelationshipLinkMeta = computed(() => {
    if (!sourceRelationship.value || !sourceRelationship.value.oppositeSchemas[0])
        return null;
    return { details: sourceRelationship.value, label: sourceRelationship.value.oppositeSchemas[0].singular };
});

const subgridData = new DataSource({
	loadMode: 'raw',
	cacheRawData: true,
	key: 'id',
	load: async (loadOptions) => {
		if (!sourceRelationship.value)
            return [];
        
        let fetchRequest = await $http.get('/mote', { params: {
            type: sourceRelationship.value.oppositeSchemas[0].type,
            from: props.parentRow.id,
            relation_schema: sourceRelationship.value.relationSchema.type 
        }});
        // TODO: How do we deal with the difference between mote and relation here? Right now we just discard all relation payloads and use only the mote payloads.
		let result = fetchRequest.data.motes.map(({ payload, ...datum }) => ({
			...(payload || {}),
			...datum
		}));
		// TODO: We don't fetch further expanded relationships here.
		console.log("Fetched subgrid data", result);
		return result;
	},
	insert: async (values) => {
        // If the values included an id or any other reserved fields, just silently drop/ignore it.
		for (const key of fieldNamesReserved) {
			if (values[key])
				delete values[key];
		}
		
		// TODO: Figure out how we can handle multiple schemas here!
		const schema = SchemaCache.getSchemaByType(sourceRelationship.value.oppositeSchemas[0].type);
		if (!schema) {
			throw new Error('Could not find schema for insert; save aborted!');
		}
		const groupId = props.context?.group_id ?? null;
		if (!groupId) {
			throw new Error(!props.context ? 'Insert skipped - previewing component' : 'Could not find group for insert; save aborted!');
		}

		// TODO: Deal with schema actions, defaults, etc.
		const response = await $http.post('/mote', {
			group_id: groupId,
			type_id: schema.id,
			payload: values,
			// TODO: How can we add a relationship? What about the payload? What about the payload of the relationship?
			relation_schema: sourceRelationship.value.relationSchema.type,
			relation_payload: {},
			from: props.parentRow.id,
		});
		return response.data;
	},
	update: async (key, values) => {
        // TODO: This is a hack to get the original item. Once we use the proper non-raw data source, check how this is supposed to properly work!
		const originalItem = subgridData['_items'].find(item => item.id == key);
		if (!originalItem) {
			throw new Error('Could not find original item for update; save aborted!');
		}
		
		const schema = SchemaCache.getSchemaById(originalItem.type_id);
		if (!schema) {
			throw new Error('Could not find schema for update; save aborted!');
		}
		const groupId = props.context?.group_id ?? null;
		if (!groupId) {
			throw new Error(!props.context ? 'Update skipped - previewing component' : 'Could not find group for update; save aborted!');
		}
		// TODO: Deal with schema actions, defaults, etc.
		
		// Create our updated PATCH data, where we include sequence and base fields and put the custom values in payload.
		const patchData = {
			id: key,
			sequence: originalItem.sequence + 1,
			payload: {},
		};
		for (const [key, value] of Object.entries(values)) {
			if (fieldNamesReserved.includes(key))		// Ignore attempted changes to non-field entries.
				continue;
			// TODO: Drop any values that don't resolve to a field we understand on this schema.
			patchData.payload[key] = value;
		}

		const response = await $http.patch(`/mote/${key}`, patchData);
		return response.data;
	},
	remove: async (key) => {
        // TODO: This is a hack to get the original item. Once we use the proper non-raw data source, check how this is supposed to properly work!
		const originalItem = subgridData['_items'].find(item => item.id == key);
		if (!originalItem) {
			throw new Error('Could not find original item for delete; delete aborted!');
		}

		const schema = SchemaCache.getSchemaById(originalItem.type_id);
		if (!schema) {
			throw new Error('Could not find schema for delete; delete aborted!');
		}
		const groupId = props.context?.group_id ?? null;
		if (!groupId) {
			throw new Error(!props.context ? 'Delete skipped - previewing component' : 'Could not find group for delete; delete aborted!');
		}

		const response = await $http.delete(`/mote/${key}`, { data: { sequence: originalItem.sequence } });
		return response.data;
	}
});

// Deal with file uploads setting appropriate return values for our field data.
const onFileUploaded = (data, { request }) => {
	let jsonResponse = JSON.parse(request.response);
	if (jsonResponse.id) {
		data.setValue(stripFileFieldData(jsonResponse));
	}
}
// Deal with file value changes that are not uploads - in this case, only handling removal of files.
const onFileValueChanged = (data, { value, previousValue }) => {
	if (value.length == 0 && previousValue.length > 0) {
		data.setValue(null);
	}
}

// Deal with reference editor value updates from selections / deselections.
const onReferenceValueChanged = (data, event) => {
	console.log("Reference editor updated = ", data, event);
	data.setValue(event);
}

// Deal with a click from a reference link in a datagrid cell.
const onReferenceClicked = (item) => {
	// NB: Slightly changed from base cause we only get unexpanded details.
	// Query API to determine best way to display this item.
	$http.get(`/mote/${item.mote_id}/viewport?from_group=${props.context.group_id}`).then(response => {
		console.log("Viewport for reference = ", response.data);
		router.push({
			path: response.data.path.startsWith('/') ? response.data.path : `/${response.data.path}`,
			query: { open: item.mote_id, schema: response.data.original_type_id },
		});
	}).catch(error => {
		console.error("Error getting viewport for reference:", error);
		if (error.response && error.response.data && error.response.data.message) {
			dxAlert("Error getting viewport for reference: "+ error.response.data.message, "Unable to display reference");
		} else {
			dxAlert("Error getting viewport for reference: " + error.message, "Unable to display reference");
		}
	});
}

watch(props, async () => {
	// Initialize our columns as defaulting from the schema, with any overrides from the column settings.
	dataColumns.value.length = 0;
	await SchemaCache.waitForData();
	// const columnDefaults = props.settings && props.settings.columns ? props.settings.columns : {};
    const columnDefaults = {};
	// const rootSchema = props.settings.source ? SchemaCache.getSchemaByType(props.settings.source) : null;
    const subgridSchema = sourceRelationship.value.oppositeSchemas[0];							// TODO: Figure out multi-source subgrids!
	if (subgridSchema) {
		console.log('Subgrid: Schema', subgridSchema);
		dataColumns.value = getDatagridColumnsFromSchema(subgridSchema, columnDefaults, false, 999);		// TODO: Stop column expansion right now.
		console.log('Subgrid: Initialized columns', dataColumns.value);
	}

	subgridData.load();
	if (subgridComponent.value && subgridComponent.value.instance)
		subgridComponent.value.instance.refresh();

}, {
	immediate: true
});

// Handlers for our replacement add/link row buttons.
const addNewRowClicked = () => {
    subgridComponent.value.instance.addRow();
};
const linkExistingRowClicked = () => {
    showReferenceSelector.value = true;
};
const executeLinkExistingRow = () => {
	if (!lastReferenceSelected.value || !lastReferenceSelected.value[0] || !lastReferenceSelected.value[0].mote_id) {
		dxAlert("No reference selected - please choose an item to link to and try again.", "Linking error");
		return;
	}
	showReferenceSelector.value = false;
	
	let parentMoteId = props.parentRow.id;
	let targetMoteId = lastReferenceSelected.value[0].mote_id;
	$http.post(`/mote/${parentMoteId}/linkto/${targetMoteId}`, {
		relation_schema: sourceRelationship.value.relationSchema.type,
		relation_payload: {},
	}).then(response => {
		console.log("Linking response:", response.data);
		subgridData.load();
		if (subgridComponent.value && subgridComponent.value.instance)
			subgridComponent.value.instance.refresh();
	}).catch(error => {
		console.error("Error linking existing row:", error);
		dxAlert("Error linking existing row: " + (error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message), "Linking error");
	});
}


</script>
